import { useCallback, useMemo, useRef, useState } from "react";
import { DISCOUNT_ERR, NO_STOCK, fefoAllocate, pickLots, priceCart, rateBp, toMinor } from "@/lib/money";

export function usePosCart({ productsById, business, today, inventory = false }) {
  const dp = business.decimals;
  const inclusive = business.tax_inclusive;
  const pharmacy = business.business_type === "pharmacy";
  const [state, setState] = useState({ lines: [], cartDiscount: null });
  const ref = useRef(state);

  const commit = (next) => {
    ref.current = next;
    setState(next);
  };

  const raw = useCallback(
    (lines) =>
      lines.map((l) => {
        const p = productsById[l.product_id];
        return { unit_minor: toMinor(p.price, dp), qty: l.qty, rate_bp: rateBp(p.effective_tax_rate), discount: l.discount };
      }),
    [productsById, dp]
  );

  const tryCommit = (next) => {
    try {
      priceCart(raw(next.lines), next.cartDiscount, dp, inclusive);
      commit(next);
      return null;
    } catch (e) {
      return e.message;
    }
  };

  const stockError = (product, qty) => {
    if (!pharmacy) return null;
    const { available, ok } = inventory ? pickLots(product, qty, today, true) : fefoAllocate(product.batches, qty, today);
    if (available === 0) return NO_STOCK;
    if (!ok) return `Only ${available} in valid stock for ${product.name}`;
    return null;
  };

  const add = (product) => {
    const cur = ref.current;
    const existing = cur.lines.find((l) => l.product_id === product.id);
    const qty = (existing?.qty || 0) + 1;
    const err = stockError(product, qty);
    if (err) return err;
    const lines = existing
      ? cur.lines.map((l) => (l.product_id === product.id ? { ...l, qty } : l))
      : [...cur.lines, { product_id: product.id, qty: 1, discount: null }];
    return tryCommit({ ...cur, lines }) && commitDropping({ ...cur, lines }, product.id);
  };

  // If a qty change makes an existing discount too big, drop it rather than block the sale.
  const commitDropping = (next, pid) => {
    let n = { ...next, lines: next.lines.map((l) => (l.product_id === pid ? { ...l, discount: null } : l)) };
    if (!tryCommit(n)) return "Discount removed — it exceeded the new amount";
    n = { ...n, cartDiscount: null };
    return tryCommit(n) || "Cart discount removed — it exceeded the new amount";
  };

  const setQty = (pid, qty) => {
    const cur = ref.current;
    if (!Number.isInteger(qty) || qty < 1) return "Quantity must be at least 1";
    const err = stockError(productsById[pid], qty);
    if (err) return err;
    const next = { ...cur, lines: cur.lines.map((l) => (l.product_id === pid ? { ...l, qty } : l)) };
    const e = tryCommit(next);
    return e === DISCOUNT_ERR ? commitDropping(next, pid) : e;
  };

  const remove = (pid) => {
    const cur = ref.current;
    const next = { ...cur, lines: cur.lines.filter((l) => l.product_id !== pid) };
    if (tryCommit(next)) commit({ ...next, cartDiscount: null });
  };

  const setLineDiscount = (pid, discount) =>
    tryCommit({ ...ref.current, lines: ref.current.lines.map((l) => (l.product_id === pid ? { ...l, discount } : l)) });

  const setCartDiscount = (cartDiscount) => tryCommit({ ...ref.current, cartDiscount });

  const clear = () => commit({ lines: [], cartDiscount: null });

  const computed = useMemo(() => {
    const lines = state.lines.filter((l) => productsById[l.product_id]);
    try {
      const totals = priceCart(raw(lines), state.cartDiscount, dp, inclusive);
      const rows = lines.map((l, i) => ({
        ...l,
        product: productsById[l.product_id],
        calc: totals.lines[i],
        allocations: inventory
          ? pickLots(productsById[l.product_id], l.qty, today, pharmacy).allocations
          : pharmacy ? fefoAllocate(productsById[l.product_id].batches, l.qty, today).allocations : [],
      }));
      return { rows, totals };
    } catch {
      return { rows: [], totals: null };
    }
  }, [state, productsById, raw, dp, inclusive, pharmacy, today, inventory]);

  return {
    ...computed,
    lines: state.lines,
    cartDiscount: state.cartDiscount,
    itemCount: state.lines.reduce((a, l) => a + l.qty, 0),
    add, setQty, remove, setLineDiscount, setCartDiscount, clear,
  };
}
